import { ChoiceCard } from '@eventbrite/design-ops-ds';
import { Horizontal } from '@eventbrite/eds-layout';
import { DisplayAmount } from '@eventbrite/eds-read-more';
import React, { FunctionComponent } from 'react';
import { TicketStatus } from '../../../models/tickets';
import Divider, { DividerDirection } from '../../divider/Divider';
import TicketImageThumbnail from '../../ticketImageThumbnail';
import { TicketCommonProps } from '../constants';
import { TicketContentProps } from '../TicketContent';
import TicketDescription from '../TicketDescription';
import TicketName from '../TicketName';
import type { TicketPriceProps } from '../TicketPrice';
import TicketPrice from '../TicketPrice';
import TicketQuantityPicker, {
    TicketQuantityPickerProps,
} from '../TicketQuantityPicker';
import TicketRemainingTicketsCount from '../TicketRemainingTicketsCount';
import type { TicketSalesEndDateProps } from '../TicketSalesEndDate';
import TicketSalesEndDate from '../TicketSalesEndDate';
import { getDonationChangeActionPayload } from '../utils';
import './TicketDisplayCardContentFullSize.scss';

export interface TicketDisplayCardContentFullSizeProps
    extends Pick<TicketCommonProps, 'description'>,
        Pick<
            TicketPriceProps,
            'feesAndTaxes' | 'price' | 'priceWithoutDiscounts'
        >,
        Pick<
            TicketSalesEndDateProps,
            'formattedSalesEndDate' | 'hideSaleDates'
        >,
        Pick<
            TicketQuantityPickerProps,
            | 'formattedSalesStartDate'
            | 'isDonation'
            | 'name'
            | 'onQuantityChange'
            | 'selectableQuantities'
            | 'selectedQuantity'
            | 'ticketId'
            | 'ticketStatus'
        >,
        Pick<
            TicketContentProps,
            | 'currencyFormat'
            | 'currencySymbol'
            | 'donationAmount'
            | 'isCurrencySuffix'
        > {
    image?: {
        ticketName: string;
        thumbnailUrl: string;
        fullSizeUrl: string;
    };
    remainingQuantity: TicketCommonProps['quantityRemaining'];
    onDonationChange: (payload: {
        amount: number;
        id: string;
        variantId?: string;
    }) => void;
}

const isCardDisabledByStatus: Record<TicketStatus, boolean> = {
    [TicketStatus.onSale]: false,
    [TicketStatus.salesEndSoon]: false,
    [TicketStatus.salesEnded]: true,
    [TicketStatus.salesNotStarted]: true,
    [TicketStatus.salesStartAfter]: true,
    [TicketStatus.salesStartSoon]: true,
    [TicketStatus.soldOut]: true,
    [TicketStatus.unavailable]: true,
};

const TicketDisplayCardContentFullSize: FunctionComponent<
    TicketDisplayCardContentFullSizeProps
> = ({
    currencyFormat,
    currencySymbol,
    description,
    donationAmount,
    feesAndTaxes,
    formattedSalesEndDate,
    formattedSalesStartDate,
    hideSaleDates,
    image,
    isCurrencySuffix,
    isDonation,
    name,
    onDonationChange,
    onQuantityChange,
    price,
    priceWithoutDiscounts,
    remainingQuantity,
    selectableQuantities,
    selectedQuantity,
    ticketId,
    ticketStatus,
}) => {
    const isSoldOut = ticketStatus === TicketStatus.soldOut;
    const isCardDisabled = isCardDisabledByStatus[ticketStatus];
    const isCardSelected = selectedQuantity > 0;

    const priceCountDateAndThumbnailFragment = image ? (
        <Horizontal className="eds-l-pad-top-4 eds-l-pad-hor-4">
            <div className="ticket-display-card-content-full-size__price-date-and-ticket-count-column">
                <TicketPrice
                    price={price}
                    feesAndTaxes={feesAndTaxes}
                    priceWithoutDiscounts={priceWithoutDiscounts}
                    disabled={isCardDisabled}
                />
                <TicketSalesEndDate
                    className="eds-l-pad-top-1"
                    formattedSalesEndDate={formattedSalesEndDate}
                    ticketStatus={ticketStatus}
                    hideSaleDates={hideSaleDates}
                />
                <TicketRemainingTicketsCount
                    className="eds-l-pad-top-4"
                    isSoldOut={isSoldOut}
                    numTicketsRemaining={remainingQuantity}
                />
            </div>
            <div className="ticket-display-card-content-full-size__thumbnail">
                <TicketImageThumbnail
                    title={image.ticketName}
                    fullImageUrl={image.fullSizeUrl}
                    thumbnailUrl={image.thumbnailUrl}
                />
            </div>
        </Horizontal>
    ) : (
        <>
            <Horizontal className="eds-l-pad-top-4 eds-l-pad-hor-4 ticket-display-card-content-full-size__price-and-ticket-count-line">
                <TicketPrice
                    price={price}
                    feesAndTaxes={feesAndTaxes}
                    priceWithoutDiscounts={priceWithoutDiscounts}
                    disabled={isCardDisabled}
                />
                <TicketRemainingTicketsCount
                    isSoldOut={isSoldOut}
                    numTicketsRemaining={remainingQuantity}
                />
            </Horizontal>
            <TicketSalesEndDate
                className="eds-l-pad-top-1 eds-l-pad-hor-4"
                formattedSalesEndDate={formattedSalesEndDate}
                ticketStatus={ticketStatus}
                hideSaleDates={hideSaleDates}
            />
        </>
    );

    const handleDonationChange = (amount: string) => {
        onDonationChange?.(
            getDonationChangeActionPayload(ticketId, amount, currencyFormat),
        );
    };
    const baseTicketQuantityPickerProps: Omit<
        TicketQuantityPickerProps,
        'ticketStatus' | 'formattedSalesStartDate' | 'className'
    > = {
        currencyFormat,
        currencySymbol,
        donationAmount,
        isDisabled: ticketStatus === TicketStatus.soldOut,
        isCurrencySuffix,
        isDonation,
        name,
        onDonationChange: handleDonationChange,
        onQuantityChange,
        selectableQuantities,
        selectedQuantity,
        ticketId,
    };
    const ticketQuantityPickerProps: TicketQuantityPickerProps =
        ticketStatus === TicketStatus.salesNotStarted ||
        ticketStatus === TicketStatus.salesStartSoon
            ? {
                  ...baseTicketQuantityPickerProps,
                  ticketStatus,
                  // `formattedSalesStartDate` is always defined when when ticket status is salesNotStarted or
                  // salesStartSoon. For some reason the TypeScript compiler in v4.5.5 is not able of properly narrowing
                  // the type of formattedSalesStartDate relying on the `if` above, so we have to manually cast the value
                  // here.
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  formattedSalesStartDate: formattedSalesStartDate!,
              }
            : {
                  ...baseTicketQuantityPickerProps,
                  ticketStatus,
              };

    return (
        <ChoiceCard
            testId="ticket-display-card-content-full-size"
            className="ticket-display-card-content-full-size eds-l-pad-vert-4 eds-l-mar-top-4"
            disabled={isCardDisabled}
            selected={isCardSelected}
        >
            <Horizontal className="eds-l-pad-hor-4 eds-l-pad-bot-4">
                <TicketName
                    className="ticket-display-card-content-full-size__ticket-name"
                    disabled={isCardDisabled}
                >
                    {name}
                </TicketName>
                <TicketQuantityPicker {...ticketQuantityPickerProps} />
            </Horizontal>
            <Divider direction={DividerDirection.horizontal} />
            {priceCountDateAndThumbnailFragment}
            <TicketDescription
                className="eds-l-pad-top-4 eds-l-pad-hor-4"
                collapsedDisplayAmount={DisplayAmount.small}
            >
                {description}
            </TicketDescription>
        </ChoiceCard>
    );
};

export default TicketDisplayCardContentFullSize;
